<template>
  <div class="page-container">
    <div class="page-header">
      <h1 class="page-title">庫存異動紀錄</h1>
    </div>

    <div class="page-toolbar">
      <div class="filter-group">
        <select v-model="filters.warehouseId" @change="fetchBatches" class="form-control">
          <option value="">全部倉庫</option>
          <option v-for="w in warehouses" :key="w.id" :value="w.id">{{ w.warehouseName }}</option>
        </select>
      </div>
      <div class="filter-group">
        <select v-model="filters.transactionType" @change="fetchBatches" class="form-control">
          <option value="">全部類型</option>
          <option value="purchase_in">進貨入庫</option>
          <option value="transfer_out">調撥出庫</option>
          <option value="transfer_in">調撥入庫</option>
          <option value="order_deduct">訂單扣減</option>
          <option value="order_reserve">訂單預扣</option>
          <option value="order_cancel">訂單取消還原</option>
          <option value="adjustment_add">人工調增</option>
          <option value="adjustment_sub">人工調減</option>
          <option value="sale_out">銷售出庫</option>
          <option value="return_in">退貨入庫</option>
        </select>
      </div>
      <div class="filter-group">
        <input v-model="filters.startDate" type="date" class="form-control" @change="fetchBatches" />
      </div>
      <div class="filter-group">
        <input v-model="filters.endDate" type="date" class="form-control" @change="fetchBatches" />
      </div>
    </div>

    <div class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th style="width: 36px"></th>
            <th>時間</th>
            <th>倉庫</th>
            <th>異動類型</th>
            <th>關聯單號</th>
            <th>商品品項</th>
            <th>異動數量合計</th>
            <th>操作者</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading"><td colspan="9" class="text-center">載入中...</td></tr>
          <tr v-else-if="!batches.length"><td colspan="9" class="text-center text-muted">尚無異動紀錄</td></tr>

          <template v-for="batch in batches" :key="batch.batchKey">
            <!-- 批次列 -->
            <tr class="batch-row" :class="{ 'batch-expanded': expandedKeys.has(batch.batchId) }" @click="toggleExpand(batch)">
              <td class="expand-cell">
                <i :class="expandedKeys.has(batch.batchId) ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'" class="expand-icon"></i>
              </td>
              <td class="text-nowrap">{{ formatDate(batch.createdAt) }}</td>
              <td>{{ batch.warehouseName }}</td>
              <td>
                <span :class="['badge', typeClass(batch.transactionType)]">{{ typeLabel(batch.transactionType) }}</span>
              </td>
              <td>
                <span v-if="batch.referenceNo" class="ref-no">{{ batch.referenceNo }}</span>
                <span v-else class="text-muted text-small">手動調整</span>
              </td>
              <td>
                <span class="item-count-badge">{{ batch.itemCount }} 項</span>
              </td>
              <td>
                <strong :class="batch.totalQuantityChange > 0 ? 'text-success' : 'text-danger'">
                  {{ batch.totalQuantityChange > 0 ? '+' : '' }}{{ batch.totalQuantityChange }}
                </strong>
              </td>
              <td class="text-small">
                <span v-if="batch.operatorName">
                  {{ batch.operatorName }}<br>
                  <span class="text-muted">{{ batch.operatorUsername }}</span>
                </span>
                <span v-else class="text-muted">-</span>
              </td>
              <td>
                <button class="btn btn-ghost btn-sm" @click.stop="toggleExpand(batch)">
                  {{ expandedKeys.has(batch.batchId) ? '收起' : '查看明細' }}
                </button>
              </td>
            </tr>

            <!-- 展開的明細列 -->
            <tr v-if="expandedKeys.has(batch.batchId)" class="detail-row">
              <td colspan="9" class="detail-cell">
                <div v-if="detailLoading[batch.batchId]" class="detail-loading">
                  <i class="ri-loader-4-line spin"></i> 載入中...
                </div>
                <table v-else class="detail-table">
                  <thead>
                    <tr>
                      <th>商品名稱</th>
                      <th>規格 SKU</th>
                      <th>異動數量</th>
                      <th>異動前</th>
                      <th>異動後</th>
                      <th>備註</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="!(detailData[batch.batchId] || []).length">
                      <td colspan="6" class="text-center text-muted">無明細資料</td>
                    </tr>
                    <tr v-for="item in (detailData[batch.batchId] || [])" :key="item.id">
                      <td>{{ item.productName }}</td>
                      <td><code>{{ item.variantSku }}</code><span v-if="formatAttrs(item.variantAttributes)" class="attr-label">{{ formatAttrs(item.variantAttributes) }}</span></td>
                      <td>
                        <strong :class="item.quantityChange > 0 ? 'text-success' : 'text-danger'">
                          {{ item.quantityChange > 0 ? '+' : '' }}{{ item.quantityChange }}
                        </strong>
                      </td>
                      <td>{{ item.quantityBefore }}</td>
                      <td>{{ item.quantityAfter }}</td>
                      <td class="text-small text-muted">{{ item.note || '-' }}</td>
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <ListPagination
      v-if="pagination"
      :page="filters.page"
      :total-pages="pagination.totalPages"
      :total="pagination.totalItems"
      @change="changePage"
    />
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { warehouseService } from '@/api/modules/warehouses.js';

const warehouses = ref([]);
const batches = ref([]);
const loading = ref(false);
const pagination = ref(null);
const filters = ref({ warehouseId: '', transactionType: '', startDate: '', endDate: '', page: 1, pageSize: 20 });

// 展開狀態
const expandedKeys = ref(new Set());
const detailLoading = reactive({});
const detailData = reactive({});

const TYPE_LABELS = {
  purchase_in:    '進貨入庫',
  transfer_out:   '調撥出庫',
  transfer_in:    '調撥入庫',
  adjustment_add: '人工調增',
  adjustment_sub: '人工調減',
  sale_out:       '銷售出庫',
  return_in:      '退貨入庫',
  order_deduct:   '訂單扣減',
  order_reserve:  '訂單預扣',
  order_cancel:   '訂單取消還原',
};
const TYPE_CLASSES = {
  purchase_in:    'badge-success',
  transfer_out:   'badge-warning',
  transfer_in:    'badge-info',
  adjustment_add: 'badge-success',
  adjustment_sub: 'badge-danger',
  sale_out:       'badge-danger',
  return_in:      'badge-primary',
  order_deduct:   'badge-danger',
  order_reserve:  'badge-warning',
  order_cancel:   'badge-success',
};

const typeLabel = (t) => TYPE_LABELS[t] || t;
const typeClass = (t) => TYPE_CLASSES[t] || 'badge-secondary';
const formatDate = (d) => d ? new Date(d).toLocaleString('zh-TW', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-';
const formatAttrs = (attrs) => {
  if (!attrs || typeof attrs !== 'object') return '';
  return Object.values(attrs).filter(Boolean).join(' / ');
};

async function fetchWarehouses() {
  try {
    const res = await warehouseService.getWarehouses({ status: 'active' });
    warehouses.value = res.data?.items || [];
  } catch (e) { console.error(e); }
}

async function fetchBatches() {
  loading.value = true;
  expandedKeys.value.clear();
  try {
    const params = { page: filters.value.page, pageSize: filters.value.pageSize };
    if (filters.value.warehouseId) params.warehouseId = filters.value.warehouseId;
    if (filters.value.transactionType) params.transactionType = filters.value.transactionType;
    if (filters.value.startDate) params.startDate = filters.value.startDate;
    if (filters.value.endDate) params.endDate = new Date(new Date(filters.value.endDate).getTime() + 86400000).toISOString().slice(0, 10);
    const res = await warehouseService.getStockTransactionBatches(params);
    batches.value = res.data?.items || [];
    pagination.value = res.data?.pagination || null;
  } catch (e) { console.error(e); } finally { loading.value = false; }
}

async function toggleExpand(batch) {
  const key = batch.batchId;
  if (expandedKeys.value.has(key)) {
    expandedKeys.value.delete(key);
    expandedKeys.value = new Set(expandedKeys.value);
    return;
  }
  expandedKeys.value = new Set([...expandedKeys.value, key]);

  // 若已有快取，不重新載入
  if (detailData[key]) return;

  detailLoading[key] = true;
  try {
    // batchId 為 null（舊紀錄），補傳備援識別欄位
    const params = key
      ? { batchId: key }
      : {
          referenceId: batch.referenceId,
          warehouseId: batch.warehouseId ?? undefined,
          transactionType: batch.transactionType,
        };
    const res = await warehouseService.getStockTransactionBatchDetail(params);
    detailData[key] = res.data || [];
  } catch (e) {
    console.error(e);
    detailData[key] = [];
  } finally {
    detailLoading[key] = false;
  }
}

function changePage(p) { filters.value.page = p; fetchBatches(); }

onMounted(() => { fetchWarehouses(); fetchBatches(); });
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.table-wrapper table { min-width: 900px; }

/* 批次主列 */
.batch-row {
  cursor: pointer;
  transition: background 0.15s;
}
.batch-row:hover { background: #f8f9fa; }
.batch-row.batch-expanded { background: #f0f6ff; }

/* 展開圖示 */
.expand-cell { padding: 10px 8px 10px 12px; }
.expand-icon { font-size: 16px; color: #888; transition: transform 0.2s; }

/* 展開明細區 */
.detail-row > td { padding: 0; }
.detail-cell {
  padding: 0 0 12px 36px;
  background: #f8fbff;
  border-bottom: 2px solid #d0e4ff;
}
.detail-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  color: #888;
  font-size: 13px;
}

/* 明細子表格 */
.detail-table {
  width: 100%;
  border-collapse: collapse;
  margin: 0;
  font-size: 13px;
}
.detail-table th {
  background: #e8f0fe;
  color: #1a73e8;
  font-weight: 600;
  padding: 8px 12px;
  text-align: left;
  border-bottom: 1px solid #c5d8f8;
}
.detail-table td {
  padding: 8px 12px;
  border-bottom: 1px solid #eef2ff;
  color: #333;
}
.detail-table tbody tr:last-child td { border-bottom: none; }

/* 標籤與標記 */
.badge { padding: 2px 8px; border-radius: 20px; font-size: 12px; font-weight: 500; }
.badge-primary { background: #e3f0ff; color: #1a73e8; }
.badge-secondary { background: #f0f0f0; color: #666; }
.badge-info { background: #e3f8ff; color: #0984e3; }
.badge-success { background: #e6f9ee; color: #27ae60; }
.badge-danger { background: #fde8e8; color: #e74c3c; }
.badge-warning { background: #fff8e1; color: #f39c12; }

.item-count-badge {
  display: inline-block;
  background: #eff3f8;
  color: #555;
  border-radius: 12px;
  padding: 2px 10px;
  font-size: 12px;
  font-weight: 600;
}
.ref-no { font-family: monospace; font-size: 13px; color: #1a73e8; font-weight: 600; }
.attr-label { margin-left: 6px; color: #888; font-size: 11px; }

.text-success { color: #27ae60; }
.text-danger { color: #e74c3c; }
.text-muted { color: #999; }
.text-center { text-align: center; padding: 20px; }
.text-nowrap { white-space: nowrap; }
.text-small { font-size: 12px; }

.btn { padding: 8px 16px; border-radius: 6px; cursor: pointer; font-size: 14px; border: none; background: transparent; }
.btn-ghost { color: #1a73e8; padding: 4px 10px; font-size: 12px; border-radius: 4px; }
.btn-ghost:hover { background: #e8f0fe; }
.btn-outline { background: transparent; border: 1px solid #1a73e8; color: #1a73e8; }
.btn-sm { padding: 4px 10px; font-size: 12px; border-radius: 4px; }
.btn:disabled { opacity: .6; cursor: not-allowed; }

.pagination-wrapper { display: flex; align-items: center; gap: 10px; margin-top: 16px; justify-content: flex-end; }
.pagination-info, .pagination-page { color: #666; font-size: 13px; }

@keyframes spin { to { transform: rotate(360deg); } }
.spin { display: inline-block; animation: spin 1s linear infinite; }
</style>
